import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import { AxiosError } from "axios"
import {ILoginRequest, IUser} from "@/src/interfaces/auth.interface";
import {authService} from "@/src/services/auth.service";
import {merchantUserService} from "@/src/services/merchant-user.service";

interface AuthState {
    user: IUser | null
    token: string | null
    isLoading: boolean
    error: string | null
    /**
     * Whether the app has finished attempting to restore a session from
     * localStorage on this load. Route guards must wait for this before
     * deciding to redirect — otherwise a real logged-in user gets bounced
     * to /login for the one render before hydration runs.
     */
    isHydrated: boolean
}

const initialState: AuthState = {
    user: null,
    token: null,
    isLoading: false,
    error: null,
    isHydrated: false,
}

export const loginUser = createAsyncThunk(
    "public/login",
    async (payload: ILoginRequest, { rejectWithValue }) => {
        try {
            const response = await authService.login(payload)
            return response.data
        } catch (err) {
            const error = err as AxiosError<{ message: string }>
            return rejectWithValue(error.response?.data?.message ?? "Login failed. Please try again.")
        }
    }
)

/**
 * Which dashboard is signing out. Both endpoints revoke the same Sanctum
 * token, but the merchant API exposes its own route (routes/merchant.php) and
 * logs the event to its own channel, so each dashboard calls its own.
 */
export type LogoutScope = "admin" | "merchant"

/**
 * Revokes the session server-side, then always clears local auth state —
 * even if the API call fails (e.g. the token already expired), the user
 * should still end up logged out on this device.
 */
export const logoutUser = createAsyncThunk(
    "auth/logout",
    async (scope: LogoutScope | void, { dispatch }) => {
        try {
            if (scope === "merchant") await merchantUserService.logout()
            else await authService.logout()
        } finally {
            dispatch(logout())
        }
    }
)

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logout(state) {
            state.user = null
            state.token = null
            if (typeof window !== "undefined") {
                localStorage.removeItem("token")
                localStorage.removeItem("user")
            }
        },
        // Used on app load to restore state from localStorage — see AuthHydrator.
        hydrateAuth(state, action: PayloadAction<{ user: IUser; token: string }>) {
            state.user = action.payload.user
            state.token = action.payload.token
            state.isHydrated = true
        },
        // Called when there was nothing to restore, so guards can stop waiting.
        authReady(state) {
            state.isHydrated = true
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload.user
                state.token = action.payload.token
                state.isHydrated = true
                if (typeof window !== "undefined") {
                    localStorage.setItem("token", action.payload.token)
                    localStorage.setItem("user", JSON.stringify(action.payload.user))
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
            .addCase(logoutUser.pending, (state) => {
                state.isLoading = true
            })
            .addMatcher(logoutUser.settled, (state) => {
                state.isLoading = false
            })
    },
})

export const { logout, hydrateAuth, authReady } = authSlice.actions
export default authSlice.reducer